/**
 * Слайд-капча для auth-форм ведущих (И8).
 *
 * Запрашивает `GET /api/captcha/slide` (через [`getCaptcha`](../api.ts)), рендерит
 * фон с «дыркой» и кусок-пазл, который пользователь двигает горизонтально (слайдер
 * на pointer events — без сторонних зависимостей). При отпускании ползунок дергает
 * `onChange` с решением `{captchaId, captchaX}`. Кнопка «обновить» запрашивает новую.
 *
 * Если капча недоступна (`GET` вернул 503 — CAPTCHA_ENABLED=0, или сеть), компонент
 * рендерит `null` и зовёт `onChange(null)`: форма сабмитится БЕЗ капчи, а серверные
 * проверки в этом режиме — no-op (см. captcha.go в server). Это сохраняет обратную
 * совместимость dev/нагрузочного стенда.
 *
 * Геометрия: фон 300×220, кусок 60×60 (нативные пиксели сервера — см. captcha.go).
 * Компонент масштабируется под ширину контейнера (width:100%), пересчитывая native
 * координату `captchaX` по измеренному масштабу — корректно на любом экране.
 *
 * Визуальное подтверждение (Фаза 9, П5): после отпускания ползунка компонент делает
 * ПРЕД-САБМИТ серверную проверку (`verifyCaptcha` → POST /api/captcha/verify). Зелёная
 * рамка `captcha__stage--ok` + ✓ появляются ТОЛЬКО при ВЕРНОМ решении (ok:true); при
 * неверном — краткий красный сигнал `captcha__stage--err` и перевыпуск новой капчи.
 * Серверная проверка в register остаётся авторитетной (verify не погашает верную капчу —
 * см. api.ts). Статус объявляется скринридерам через `aria-live` (`captcha.solved` /
 * `captcha.wrong`). Сбрасывается при `load` (новая капча) и при remount (ключ
 * `captchaNonce` в Register).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCaptcha, verifyCaptcha, type CaptchaSolution, type SlideCaptcha } from '../api';

/** Native-геометрия сервера (см. server/internal/captcha/resources.go). */
const IMAGE_W = 300;
const TILE_W = 60;

export interface CaptchaProps {
  /** Решение при отпускании ползунка; null — капча сброшена/недоступна/не решена. */
  onChange?: (solution: CaptchaSolution | null) => void;
  /** Инъекция fetch для тестов (по умолчанию — глобальный fetch). */
  fetchImpl?: typeof fetch;
}

export function Captcha({ onChange, fetchImpl }: CaptchaProps): JSX.Element | null {
  const { t } = useTranslation();
  const [cap, setCap] = useState<SlideCaptcha | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [drag, setDrag] = useState(0); // смещение ползунка в RENDERED-пикселях.
  const [dragging, setDragging] = useState(false);
  // bgLoaded — триггер перерасчёта масштаба после onLoad картинки (до того
  // clientWidth=0 и scale=1: компонент рисуется в native-размере, что корректно).
  const [bgLoaded, setBgLoaded] = useState(false);
  // П5 (Фаза 9): «решено» = сервер подтвердил ВЕРНОЕ решение (verifyCaptcha ok:true).
  // НЕ появляется при простом отпускании ползунка — только после серверной проверки.
  // `wrong` — неверное решение: краткий красный сигнал до перевыпуска новой капчи.
  // Сбрасываются при `load` (новая капча) и при remount (ключ `captchaNonce` в Register).
  const [solved, setSolved] = useState(false);
  const [wrong, setWrong] = useState(false);

  const bgRef = useRef<HTMLImageElement>(null);
  // Стартовая позиция pointer и drag на момент нажатия — для корректного драга.
  const startXRef = useRef(0);
  const startDragRef = useRef(0);

  const load = useCallback(async () => {
    setDrag(0);
    setSolved(false); // новая капча — предыдущее решение больше не актуально.
    setWrong(false);
    try {
      const c = await getCaptcha(fetchImpl);
      setCap(c);
      setUnavailable(false);
      onChange?.(null); // новая капча — старое решение больше не валидно.
    } catch {
      // 503 (отключена) или сеть: форма сабмитится без капчи (no-op на сервере).
      setCap(null);
      setUnavailable(true);
      onChange?.(null);
    }
  }, [fetchImpl, onChange]);

  useEffect(() => {
    void load();
  }, [load]);

  // Масштаб native↔rendered: измеряем клиентскую ширину bg после onLoad (до того
  // — native, 1:1). В jsdom clientWidth=0 → fallback на native, drag 1:1 (для тестов).
  const renderedW = bgLoaded ? bgRef.current?.clientWidth || IMAGE_W : IMAGE_W;
  const scale = renderedW / IMAGE_W;
  const tileRendered = TILE_W * scale;
  const maxDrag = Math.max(0, renderedW - tileRendered);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!cap || solved) return; // решено — блокируем повторный драг (не сбить верное решение).
    try {
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture может отсутствовать в окружении — игнорируем (drag всё
      // равно работает, пока pointer над элементом).
    }
    startXRef.current = e.clientX;
    startDragRef.current = drag;
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || !cap) return;
    const next = startDragRef.current + (e.clientX - startXRef.current);
    setDrag(Math.max(0, Math.min(next, maxDrag)));
  };

  const endDrag = async (): Promise<void> => {
    if (!dragging || !cap) return;
    setDragging(false);
    const captchaX = Math.round(drag / scale); // перевод rendered→native.
    const solution: CaptchaSolution = { captchaId: cap.id, captchaX };
    onChange?.(solution);
    // П5: пред-сабмит серверная проверка — зелёная галочка ТОЛЬКО при ВЕРНОМ решении.
    // (До фикса здесь было безусловное setSolved(true) — ложное одобрение при любом движении.)
    try {
      const { ok } = await verifyCaptcha(solution, fetchImpl);
      if (ok) {
        setSolved(true); // верно → зелёная рамка + ✓; слайдер блокируется (onPointerDown).
      } else {
        // Неверно → краткий красный сигнал, затем перевыпуск (неверная капча погашена).
        setWrong(true);
        window.setTimeout(() => void load(), 650);
      }
    } catch {
      // 503 (отключена — но тогда виджет и не рендерится) / 429 / сеть: не показываем
      // ни зелёный, ни красный — решение уже ушло в onChange, сервер проверит при register.
    }
  };

  // Капча недоступна — не мешаем форме (no-op режим сервера).
  if (unavailable || !cap) {
    return null;
  }

  return (
    <div className="captcha" data-testid="captcha">
      <div
        className={`captcha__stage${solved ? ' captcha__stage--ok' : ''}${
          wrong ? ' captcha__stage--err' : ''
        }`}
        data-testid="captcha-stage"
      >
        <img
          ref={bgRef}
          src={cap.bg}
          className="captcha__bg"
          alt={t('captcha.label')}
          draggable={false}
          onLoad={() => setBgLoaded(true)}
        />
        {/* Кусок-пазл двигается по горизонтали вместе с ползунком; вертикаль —
            фиксирована сервером (tileY). Размер/позиция масштабируются под контейнер. */}
        <img
          src={cap.tile}
          className="captcha__tile"
          style={{
            width: `${tileRendered}px`,
            height: `${tileRendered}px`,
            transform: `translate(${drag}px, ${cap.tileY * scale}px)`,
          }}
          alt=""
          draggable={false}
        />
        {/* П5: визуальное подтверждение решения — декоративный ✓ (скринридеры его
            игнорируют: aria-hidden; текст-статус объявляется ниже через aria-live). */}
        {solved ? (
          <span className="captcha__ok" aria-hidden="true" data-testid="captcha-ok">
            ✓
          </span>
        ) : null}
      </div>
      <div className="captcha__track">
        <div
          className="captcha__handle"
          data-testid="captcha-handle"
          role="slider"
          tabIndex={0}
          aria-label={t('captcha.slide')}
          aria-valuenow={Math.round(drag / scale)}
          aria-valuemin={0}
          aria-valuemax={IMAGE_W - TILE_W}
          style={{ transform: `translateX(${drag}px)` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
        <span className="captcha__hint">{t('captcha.slide')}</span>
        <button
          type="button"
          className="captcha__refresh"
          data-testid="captcha-refresh"
          onClick={() => void load()}
          aria-label={t('captcha.refresh')}
        >
          ↻
        </button>
      </div>
      {/* П5: aria-live объявляет статус капчи скринридерам (решена/сброшена).
          polite — не прерывает текущее озвучивание; виден только в accessibility tree. */}
      <span className="sr-only" role="status" aria-live="polite" data-testid="captcha-status">
        {solved ? t('captcha.solved') : wrong ? t('captcha.wrong') : ''}
      </span>
    </div>
  );
}
